import { h, Host } from "@stencil/core";
import { getAutoStartEnabled, isDefined, isValidTargetRect, setAutoStartEnabled } from "../utils";
import { GuidedTourBackdrop } from "./guided-tour-backdrop";
import { GuidedTourProgress } from "./guided-tour-progress";
import { debounce } from "../../../utils/debounce";
import { ButtonSize, ButtonVariant } from "../../button/button.enum";
const baseClass = "ux-guided-tour-overlay";
export class GuidedTourOverlay {
    constructor() {
        this.tourHash = "";
        this.targetRect = null;
        this.targetOffset = 20;
        this.step = null;
        this.stepIndex = 0;
        this.totalSteps = 0;
        this.autoStartSwitch = false;
        this.autoStartLabel = "Show on startup";
        this.finishTourButtonLabel = undefined;
        this.isReady = false;
        this.popoverOpened = false;
    }
    // @ts-expect-error Check for typing
    canvasElement = null;
    // @ts-expect-error Check for typing
    focusElement = null;
    // @ts-expect-error Check for typing
    popoverElement = null;
    // @ts-expect-error Check for typing
    bodyElement = null;
    handleResize() {
        this.drawCanvasDebounced();
    }
    onTargetRectChange() {
        this.drawCanvas();
    }
    onStepChange() {
        this.closePopover();
        this.drawCanvas();
        if (isDefined(this.step)) {
            this.updateBody();
            this.openPopover();
        }
    }
    openPopover = debounce(() => {
        if (isDefined(this.step)) {
            this.popoverOpened = true;
            this.focusNextButton();
        }
    }, 100);
    closePopover = () => {
        this.popoverOpened = false;
    };
    focusNextButton = debounce(() => {
        const nextButton = this.popoverElement.querySelector('[data-trigger-guided-tour="next"]');
        if (nextButton !== null) {
            nextButton.focus();
        }
    }, 100);
    drawCanvasDebounced = debounce(() => this.drawCanvas(), 10);
    drawCanvas = () => {
        const { step, canvasElement, targetRect } = this;
        if (canvasElement === null) {
            return;
        }
        canvasElement.width = window.innerWidth;
        canvasElement.height = window.innerHeight;
        const ctx = canvasElement.getContext("2d");
        if (ctx === null) {
            return;
        }
        ctx.beginPath();
        ctx.rect(0, 0, canvasElement.width, canvasElement.height);
        // @ts-expect-error Check for typing
        if (isValidTargetRect(targetRect)) {
            const targetOffset = isDefined(step) && isDefined(step.targetOffset) ? step.targetOffset : this.targetOffset;
            const hole = {
                // @ts-expect-error Check for typing
                top: targetRect.top - targetOffset,
                // @ts-expect-error Check for typing
                bottom: targetRect.top + targetRect.height + targetOffset,
                // @ts-expect-error Check for typing
                left: targetRect.left - targetOffset,
                // @ts-expect-error Check for typing
                right: targetRect.left + targetRect.width + targetOffset
            };
            ctx.moveTo(hole.left, hole.top);
            ctx.lineTo(hole.left, hole.bottom);
            ctx.lineTo(hole.right, hole.bottom);
            ctx.lineTo(hole.right, hole.top);
            ctx.lineTo(hole.left, hole.top);
        }
        ctx.closePath();
        ctx.fillStyle = "#000";
        ctx.fill();
    };
    updateBody = () => {
        if (this.bodyElement === null) {
            return;
        }
        if (isDefined(this.step)) {
            this.bodyElement.innerHTML = this.step.body || "";
        }
        else {
            this.bodyElement.innerHTML = "";
        }
    };
    componentWillLoad() {
        this.onStepChange();
    }
    componentDidLoad() {
        this.drawCanvas();
        this.updateBody();
        if (!this.isReady) {
            window.setTimeout(() => {
                this.isReady = true;
            }, 100);
        }
    }
    componentDidRender() {
        this.popoverElement.updatePlacement();
    }
    render() {
        const { step, stepIndex, autoStartSwitch, autoStartLabel, finishTourButtonLabel, popoverOpened, targetRect, totalSteps } = this;
        // @ts-expect-error Check for typing
        const hasTarget = isValidTargetRect(targetRect);
        const hasStep = isDefined(step);
        const hasNextButtonText = hasStep && typeof step.nextButtonLabel === "string";
        const hasBackButtonText = hasStep && typeof step.backButtonLabel === "string";
        const targetOffset = hasStep && isDefined(step.targetOffset) ? step.targetOffset : this.targetOffset;
        const focusStyle = hasTarget
            ? {
                // @ts-expect-error Check for typing
                top: `${targetRect.top - targetOffset}px`,
                // @ts-expect-error Check for typing
                left: `${targetRect.left - targetOffset}px`,
                // @ts-expect-error Check for typing
                width: `${targetRect.width + 2 * targetOffset}px`,
                // @ts-expect-error Check for typing
                height: `${targetRect.height + 2 * targetOffset}px`
            }
            : undefined;
        const focusElement = hasTarget ? this.focusElement : null;
        const nextButtonCustomClassNames = hasStep ? this.step.nextButtonClassNames || "" : "";
        const backButtonCustomClassNames = hasStep ? this.step.backButtonClassNames || "" : "";
        // @ts-expect-error Check for typing
        const focusFx = hasStep ? this.step.focusFx : "glow";
        const hideStepsCount = hasStep ? this.step.hideStepsCount : false;
        const hideProgressBar = hasStep ? this.step.hideProgressBar : false;
        const isStartScreen = stepIndex === -1;
        const isLastStep = stepIndex + 1 === totalSteps;
        const hasFinishButtonLabel = finishTourButtonLabel !== undefined;
        return (h(Host, { key: '7ce95fa92b7b84ba11face70c4d46a93e591c67b', class: {
                [baseClass]: true,
                [`${baseClass}--ready`]: this.isReady,
                [`${baseClass}--active`]: this.isReady && hasStep
            } }, h(GuidedTourBackdrop, { key: '199c70fa755b6ce5b73c35e837cef3dccb58b41e', targetRect: targetRect }), h("canvas", { key: 'fbfd02deff42e18574039da7ce3fd7ce8726ac9b', class: `${baseClass}__canvas`, ref: (el) => (this.canvasElement = el) }), h("div", { key: '5e912cc0d9af1697dee02d8698e582f7f8e36ad2', class: {
                [`${baseClass}__focus`]: true,
                [`${baseClass}__focus--highlight`]: focusFx !== "shadow",
                [`${baseClass}__focus--shadow`]: focusFx === "shadow"
            }, style: focusStyle,
            // @ts-expect-error Check for typing
            ref: (el) => (this.focusElement = el) }), h("ux-popover", { key: 'f62eeec2db1a3f66257b4753b94754c7ad5afba5',
            // @ts-expect-error Check for typing
            targetElement: focusElement, controlled: true, opened: popoverOpened,
            // @ts-expect-error Check for typing
            ref: (el) => (this.popoverElement = el), placement: focusElement !== null ? step?.placement || "center" : "center" }, h("ux-popover-panel", { key: '4e248d8e59b2261e3d42e4e7b3a8054cf52449c9' }, h("button", { key: 'f1de7c349c34e323bb807241f0174b3373fa7b6e', class: "ux-guided-tour__close-button", "data-trigger-guided-tour": "stop" }, h("ux-icon", { key: '7d5647590558249f1b6b2c662095ccf7da762c9c', name: "cross", weight: "normal" })), totalSteps > 0 && stepIndex >= 0 && (h("div", { key: 'b32c3cc56cf7dc68cdb453b4e02abb7cbf8b1409', class: "ux-guided-tour-overlay__steps", style: {
                display: hideStepsCount ? "none" : "block"
            }, "aria-hidden": hideStepsCount ? "true" : undefined }, stepIndex + 1, " / ", totalSteps)), h("h4", { key: '866216044145e70aa04e94ed3eaf1abdd11a1846', class: "ux-guided-tour__title ux-text ux-text-title--bold" }, step?.title), h("p", { key: '5b887441edf77d23384a6e19d689ed6bd610318e', class: "ux-guided-tour__body ux-text ux-text--normal", ref: (el) => (this.bodyElement = el) }), autoStartSwitch && (h("ux-switch-input", { key: 'ef82b3b1f1467f1327c89637adf7b95d9d16bb30', name: "settings", label: autoStartLabel, desc: "", checked: getAutoStartEnabled(this.tourHash), onUxChange: (event) => setAutoStartEnabled(event.detail, this.tourHash) })), h("div", { key: '32d97d8207899231d48689ea23e2de576696a9d9', class: "ux-guided-tour-overlay__buttons" }, (stepIndex > 0 || hasBackButtonText) && (h("ux-button", { key: 'a1732725f9c76789ec1f5bbccf93d34b74a6f4f2', class: {
                [backButtonCustomClassNames]: backButtonCustomClassNames !== ""
            }, "data-trigger-guided-tour": stepIndex > 0 ? "prev" : "stop", size: !hasBackButtonText ? ButtonSize.Icon : ButtonSize.Default, ...(hasBackButtonText && { label: step.backButtonLabel }), ...(!hasBackButtonText && { icon: "chevron-left" }) })), h("ux-button", { key: '4ddd5ae27322ed5f9c11d1851bdb5cc9ddcfb82f', class: {
                [nextButtonCustomClassNames]: nextButtonCustomClassNames !== ""
            }, "data-trigger-guided-tour": isLastStep ? "finish" : "next", variant: isStartScreen || (isLastStep && hasFinishButtonLabel)
                ? ButtonVariant.Primary
                : ButtonVariant.Default, size: (!isLastStep && !hasNextButtonText) ||
                (isLastStep && !hasNextButtonText && !hasFinishButtonLabel)
                ? ButtonSize.Icon
                : ButtonSize.Default, label: hasFinishButtonLabel && isLastStep
                ? finishTourButtonLabel
                : hasNextButtonText
                    ? step.nextButtonLabel
                    : "", ...(!hasNextButtonText && { icon: "chevron-right" }) })), !hideProgressBar && h(GuidedTourProgress, { key: '38e42edb938f13418ff1e27922c9660dadb7a8cb', total: totalSteps, step: stepIndex + 1 })))));
    }
    static get is() { return "ux-guided-tour-overlay"; }
    static get originalStyleUrls() {
        return {
            "$": ["guided-tour-overlay.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["guided-tour-overlay.css"]
        };
    }
    static get properties() {
        return {
            "tourHash": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Unique hash of the tour"
                },
                "attribute": "tour-hash",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "targetRect": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "DOMRect | null",
                    "resolved": "DOMRect | null",
                    "references": {
                        "DOMRect": {
                            "location": "global",
                            "id": "global::DOMRect"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Bounding rect of the target element"
                },
                "defaultValue": "null"
            },
            "targetOffset": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Offset between target rect and focus rect"
                },
                "attribute": "target-offset",
                "reflect": false,
                "defaultValue": "20"
            },
            "step": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "GuidedTourStep",
                    "resolved": "GuidedTourStep",
                    "references": {
                        "GuidedTourStep": {
                            "location": "import",
                            "path": "../types",
                            "id": "src/components/guided-tour/types.ts::GuidedTourStep"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The current guided-tour step data"
                },
                "defaultValue": "null"
            },
            "stepIndex": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Index of the step in the steps array"
                },
                "attribute": "step-index",
                "reflect": false,
                "defaultValue": "0"
            },
            "totalSteps": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "How many steps are available"
                },
                "attribute": "total-steps",
                "reflect": false,
                "defaultValue": "0"
            },
            "autoStartSwitch": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Display autoStart switch in popover"
                },
                "attribute": "auto-start-switch",
                "reflect": false,
                "defaultValue": "false"
            },
            "autoStartLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label for the autoStart switch"
                },
                "attribute": "auto-start-label",
                "reflect": false,
                "defaultValue": "\"Show on startup\""
            },
            "finishTourButtonLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label for the 'finish tour' button \u2013 will always be added to the last step"
                },
                "attribute": "finish-tour-button-label",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "isReady": {},
            "popoverOpened": {}
        };
    }
    static get watchers() {
        return [{
                "propName": "targetOffset",
                "methodName": "onTargetRectChange"
            }, {
                "propName": "targetRect",
                "methodName": "onTargetRectChange"
            }, {
                "propName": "step",
                "methodName": "onStepChange"
            }];
    }
    static get listeners() {
        return [{
                "name": "resize",
                "method": "handleResize",
                "target": "window",
                "capture": false,
                "passive": true
            }];
    }
}
//# sourceMappingURL=guided-tour-overlay.js.map
